<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime, Duration } from 'luxon';

import {
  ONE_MIN_INTERVAL,
  useElapsedTimeTicker
} from '@satellite/composables/useElapsedTimeTicker';
import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';

import type { DurationUnit } from 'luxon';

type ElapsedTimeVariant = 'formatted' | 'human';

/** Picks the units for a duration, given how long it is in milliseconds. */
export type ElapsedTimeUnitsResolver = (elapsedMs: number) => DurationUnit[];

const DEFAULT_UNITS: DurationUnit[] = ['days', 'hours', 'minutes'];

function formatElapsedTime(diff: Duration, format: string): string {
  return diff.toFormat(format);
}

function humanizeElapsedTime(
  diff: Duration,
  units: DurationUnit[] = DEFAULT_UNITS,
  locale?: string
): string {
  const normalized = diff.shiftTo(...units);

  const durationObject: Record<string, number> = {};

  for (const unit of units) {
    const value = Math.floor(normalized.get(unit) ?? 0);

    if (value > 0) {
      durationObject[unit] = value;
    }
  }

  // Luxon's default locale is pinned to en-US at app bootstrap, so `toHuman` needs the active
  // locale passed in or it renders "4 months and 18 days" in every language.
  const truncatedDuration = Duration.fromObject(durationObject, locale ? { locale } : {});

  return truncatedDuration.toHuman({ listStyle: 'long' });
}

function calculateElapsedTime(
  from: string,
  timezone: string | undefined,
  {
    now,
    format,
    variant,
    until,
    units,
    unitsFor,
    locale
  }: {
    format: string;
    variant: ElapsedTimeVariant;
    until: string | null | undefined;
    units: DurationUnit[];
    unitsFor?: ElapsedTimeUnitsResolver;
    now: DateTime;
    locale?: string;
  }
): string | null {
  if (!from) {
    return null;
  }

  const start = DateTime.fromISO(from, { zone: timezone });

  if (!start.isValid) {
    return null;
  }

  const rawEnd = until ? DateTime.fromISO(until, { zone: timezone }) : now.setZone(timezone);

  if (!rawEnd.isValid) {
    return null;
  }

  // `from` is a server-issued timestamp (e.g. a reserve's createDateTime) and `now` comes from the
  // viewer's own clock, so a record created only moments ago can land here before the client's
  // clock has caught up — client/server clock skew, not a logic error in the caller. Clamping to
  // `start` keeps a freshly created record's "elapsed" at zero instead of briefly rendering as a
  // negative duration (e.g. formatted "00:-01") until the skew is outrun.
  const end = rawEnd < start ? start : rawEnd;

  // Resolved here rather than by the caller so a caller whose units depend on the duration does
  // not have to keep a clock of its own just to ask what time it is.
  const resolvedUnits = unitsFor ? unitsFor(end.toMillis() - start.toMillis()) : units;

  const diff = end.diff(start, resolvedUnits, {
    conversionAccuracy: 'longterm'
  });

  if (variant === 'formatted') {
    return formatElapsedTime(diff, format);
  }

  return humanizeElapsedTime(diff, resolvedUnits, locale);
}

const {
  from,
  until = null,
  timezone,
  variant = 'formatted',
  format = 'hh:mm',
  units,
  unitsFor,
  updateInterval = ONE_MIN_INTERVAL,
  showEmptyState = true
} = defineProps<{
  /**
   * The ISO date string to calculate elapsed time from.
   */
  from: string;

  /**
   * The ISO date string to calculate elapsed time until.
   * If not provided, elapsed time is calculated against the current time.
   */
  until?: string | null;

  timezone?: string;

  variant?: ElapsedTimeVariant;

  format?: string;

  units?: DurationUnit[];

  /**
   * Picks the units from the duration itself, for a cell whose format widens with age — minutes
   * while something is new, days once it is not.
   *
   * Takes precedence over `units`. It exists so such a caller does not have to subscribe to a
   * ticker of its own purely to work out how old the value is: this component already knows.
   */
  unitsFor?: ElapsedTimeUnitsResolver;

  /**
   * Update frequency in milliseconds.
   *
   * Common values:
   * - 1000 = 1 second
   * - 10000 = 10 seconds
   * - 30000 = 30 seconds
   * - 60000 = 1 minute
   * - 300000 = 5 minutes
   * - 600000 = 10 minutes
   *
   * You can use the constants from @satellite/composables/useElapsedTimeTicker.
   * - ONE_SECOND_INTERVAL = 1000
   * - ONE_MIN_INTERVAL = 60000
   * - FIVE_MIN_INTERVAL = 300000
   * - TEN_MIN_INTERVAL = 600000
   * - ONE_HOUR_INTERVAL = 3600000
   *
   * If set to 0, the value is calculated once and does not auto-update.
   */
  updateInterval?: number;

  /**
   * Whether to show empty state text when no date is provided.
   */
  showEmptyState?: boolean;
}>();

const shouldTick = computed(() => {
  return !until && updateInterval > 0;
});

const { now } = useElapsedTimeTicker(
  () => updateInterval,
  () => shouldTick.value
);

const { locale } = useI18n();

const elapsedTime = computed(() => {
  if (!from) {
    return null;
  }

  try {
    return calculateElapsedTime(from, timezone, {
      format,
      variant,
      until,
      units: units ?? [...DEFAULT_UNITS],
      unitsFor,
      now: now.value,
      locale: locale.value
    });
  } catch (error) {
    console.warn('ElapsedTime: Error calculating elapsed time:', error);

    return null;
  }
});
</script>

<template>
  <span v-bind="$attrs" class="elapsed-time">
    <slot v-if="elapsedTime" name="default">
      {{ elapsedTime }}
    </slot>

    <slot v-else-if="showEmptyState" name="fallback">
      <presenter-value-fallback />
    </slot>
  </span>
</template>

<style scoped lang="scss">
.elapsed-time {
  // This avoids layout shift as the time progresses.
  min-inline-size: 72px;
}
</style>
